import { useState } from 'react';
import useAuthenticatedApi from '../../../Authentication/components/hooks/useAuthenticatedApi';

export type AuthType = 'none' | 'bearer' | 'basic' | 'custom_header';

export interface WebhookSettings {
    id: number;
    activationId: number;
    webhookUrl: string;
    enabled: boolean;
    authType: AuthType;
    customHeaderName: string | null;
    hasAuthConfigured: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface CreateWebhookSettingsRequest {
    webhookUrl: string;
    enabled?: boolean;
    authType?: AuthType;
    authValue?: string;
    customHeaderName?: string;
}

export interface UpdateWebhookSettingsRequest {
    webhookUrl?: string;
    enabled?: boolean;
    authType?: AuthType;
    authValue?: string;
    customHeaderName?: string;
}

export interface TestWebhookResponse {
    success: boolean;
    message: string;
}

export default function useWebhookSettings() {
    const authenticatedApi = useAuthenticatedApi();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const getWebhookSettings = async (
        activationId: number,
    ): Promise<WebhookSettings | null> => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authenticatedApi.get(
                `/activations/${activationId}/webhookSettings`,
            );
            return response.data;
        } catch (err: any) {
            if (err.response?.status === 404) {
                return null;
            }
            if (err.response?.status === 401) {
                throw err;
            }
            console.error('Error fetching webhook settings:', err);
            setError(
                err.response?.data?.message || 'Failed to fetch webhook settings',
            );
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const createWebhookSettings = async (
        activationId: number,
        data: CreateWebhookSettingsRequest,
    ): Promise<WebhookSettings | null> => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authenticatedApi.post(
                `/activations/${activationId}/webhookSettings`,
                data,
            );
            return response.data;
        } catch (err: any) {
            console.error('Error creating webhook settings:', err);
            setError(
                err.response?.data?.message || 'Failed to create webhook settings',
            );
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const updateWebhookSettings = async (
        activationId: number,
        data: UpdateWebhookSettingsRequest,
    ): Promise<WebhookSettings | null> => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authenticatedApi.put(
                `/activations/${activationId}/webhookSettings`,
                data,
            );
            return response.data;
        } catch (err: any) {
            console.error('Error updating webhook settings:', err);
            setError(
                err.response?.data?.message || 'Failed to update webhook settings',
            );
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const deleteWebhookSettings = async (
        activationId: number,
    ): Promise<boolean> => {
        setIsLoading(true);
        setError(null);
        try {
            await authenticatedApi.delete(
                `/activations/${activationId}/webhookSettings`,
            );
            return true;
        } catch (err: any) {
            console.error('Error deleting webhook settings:', err);
            setError(
                err.response?.data?.message || 'Failed to delete webhook settings',
            );
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const testWebhook = async (
        activationId: number,
    ): Promise<TestWebhookResponse> => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authenticatedApi.post(
                `/activations/${activationId}/webhookSettings/test`,
            );
            return response.data;
        } catch (err: any) {
            const message =
                err.response?.data?.message || 'Webhook test failed';
            return { success: false, message };
        } finally {
            setIsLoading(false);
        }
    };

    return {
        getWebhookSettings,
        createWebhookSettings,
        updateWebhookSettings,
        deleteWebhookSettings,
        testWebhook,
        isLoading,
        error,
    };
}

